import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '../Dashboard/Sidebar';
import { UpgradePlanModal } from '../Profile/UpgradePlanModal';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveSubscription } from '../../lib/subscriptions';
import type { SubscriptionWithPlan } from '../../lib/subscriptions';
import { AppLayoutContext } from './appLayoutContext';
import { BillingStatusBanner } from '../Billing/BillingStatusBanner';

interface AppLayoutContextValue {
  openUpgradeModal: () => void;
}

export function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionWithPlan | null>(null);
  useEffect(() => {
    if (user?.id) {
      getActiveSubscription(user.id).then(setSubscription);
    }
  }, [user?.id, location.pathname, location.search]);

  const contextValue: AppLayoutContextValue = {
    openUpgradeModal: () => setShowUpgradeModal(true),
  };

  return (
    <AppLayoutContext.Provider value={contextValue}>
      <div className="flex h-dvh overflow-hidden bg-alabaster font-sans text-charcoal antialiased selection:bg-olive/20 selection:text-olive">
        {/* Desktop Sidebar */}
        <Sidebar onUpgradeClick={() => setShowUpgradeModal(true)} />

        {/* Main Content - Outlet renders the child route */}
        <main className="relative h-dvh min-w-0 flex-1 overflow-y-auto scroll-smooth">
          {user && (
            <BillingStatusBanner
              userId={user.id}
              subscription={subscription}
            />
          )}
          <Outlet />
        </main>

        {/* Global mobile navigation is rendered in App.tsx via GlobalMobileSidebar */}

        {/* Upgrade Modal for this layout */}
        {showUpgradeModal && user && (
          <UpgradePlanModal
            currentSubscription={subscription}
            userId={user.id}
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => {
              if (user?.id) {
                getActiveSubscription(user.id).then(setSubscription);
              }
            }}
          />
        )}

      </div>
    </AppLayoutContext.Provider>
  );
}
